/**
 * Portal Variations Page
 * Manage product variation types (Color, Size, etc.) and their options
 */

import React, { useEffect, useState } from 'react';
import portalService from '../../services/portal.service';

const PortalVariations: React.FC = () => {
  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [typeName, setTypeName] = useState('');
  const [optionValue, setOptionValue] = useState('');
  const [optionColor, setOptionColor] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await portalService.getVariationTypes();
      setTypes(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreateType = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await portalService.createVariationType({ nome: typeName });
      setShowTypeModal(false);
      setTypeName('');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleDeleteType = async (id: string) => {
    if (!window.confirm('Eliminar este tipo de variacao e todas as suas opcoes?')) return;
    try {
      await portalService.deleteVariationType(id);
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleAddOption = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTypeId) return;
    try {
      await portalService.addVariationOption({
        tipoId: selectedTypeId,
        valor: optionValue,
        codigoCor: optionColor || undefined,
      });
      setShowOptionModal(false);
      setOptionValue('');
      setOptionColor('');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleDeleteOption = async (id: string) => {
    try {
      await portalService.deleteVariationOption(id);
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Variacoes de Produtos</h1>
          <p className="text-gray-600">Configure tipos de variacao (Cor, Tamanho, etc.) e as suas opcoes</p>
        </div>
        <button
          onClick={() => setShowTypeModal(true)}
          className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Tipo
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      {types.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <p className="text-lg mb-2">Nenhum tipo de variacao criado</p>
          <p className="text-sm">Crie tipos como "Cor", "Tamanho", "Material" para personalizar os seus produtos.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {types.map((type) => (
            <div key={type.ID} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{type.NOME}</h3>
                  <p className="text-sm text-gray-500">{type.opcoes?.length || 0} opcao(oes)</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setSelectedTypeId(type.ID); setShowOptionModal(true); }}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                  >
                    + Opcao
                  </button>
                  <button
                    onClick={() => handleDeleteType(type.ID)}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              {type.opcoes && type.opcoes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {type.opcoes.map((opt: any) => (
                    <div key={opt.ID} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full group">
                      {opt.CODIGO_COR && (
                        <span
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: opt.CODIGO_COR }}
                        />
                      )}
                      <span className="text-sm font-medium">{opt.VALOR}</span>
                      <button
                        onClick={() => handleDeleteOption(opt.ID)}
                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Type Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Novo Tipo de Variacao</h2>
            </div>
            <form onSubmit={handleCreateType} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Tipo *</label>
                <input
                  type="text" required value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  placeholder="Ex: Cor, Tamanho, Material, Dimensao..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowTypeModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Option Modal */}
      {showOptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                Adicionar Opcao a "{types.find(t => t.ID === selectedTypeId)?.NOME}"
              </h2>
            </div>
            <form onSubmit={handleAddOption} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
                <input
                  type="text" required value={optionValue}
                  onChange={(e) => setOptionValue(e.target.value)}
                  placeholder="Ex: Vermelho, M, 42, Algodao..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codigo de Cor (opcional)</label>
                <div className="flex gap-2">
                  <input
                    type="color" value={optionColor || '#000000'}
                    onChange={(e) => setOptionColor(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text" value={optionColor}
                    onChange={(e) => setOptionColor(e.target.value)}
                    placeholder="#FF0000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Apenas para variacoes de cor</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowOptionModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80">Adicionar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalVariations;
